import {
  Activity,
  BasketType,
  CustomPdfAppearanceModel,
  CustomPdfAppearanceSection,
  File,
  GenderType,
  Prisma,
  Role,
  User,
  UserSharedInformation,
} from "@prisma/client";
import { EditableActivity } from "./activity.types";
import { eventDateValues } from "./events-date.types";
import { EventDetails } from "./events.types";
import { EventForTickets, UserMyTicket } from "./tickets.types";
import { NullablePartial } from "./utils.types";

// User unshared fields
const unsafeUserInterfaceValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  select: {
    userSharedInformation: true,
  },
});

export type UnsafeUserInterface = Prisma.UserGetPayload<typeof unsafeUserInterfaceValues>;

export const unsafeUserValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    userSharedInformation: true,
  },
});

export type UnsafeUser = Prisma.UserGetPayload<typeof unsafeUserValues>;

type UserSharedInformationsKeys = keyof Omit<UserSharedInformation, "id" | "userId">;

export type UserSharedProtected<T> = Omit<T, UserSharedInformationsKeys> &
  NullablePartial<Pick<T, Extract<keyof T, UserSharedInformationsKeys>>>;

export const userWithRoles = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    roles: true,
  },
});

export type UserWithRoles = Prisma.UserGetPayload<typeof userWithRoles>;

export const genders = Object.values(GenderType);

export const getGenderTypeLabel = (gender: GenderType) => {
  switch (gender) {
    case GenderType.male:
      return "Masculin";
    case GenderType.female:
      return "Féminin";
    case GenderType.other:
      return "Autre";
    default:
      return "";
  }
};

export const getCivilityTypeLabel = (gender: GenderType) => {
  switch (gender) {
    case GenderType.male:
      return "Monsieur";
    case GenderType.female:
      return "Madame";
    case GenderType.other:
      return "Autre";
    default:
      return "";
  }
};

export const myUserTypeValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    roles: true,
    userSharedInformation: true,
    picture: true,
    organiser: true,
    structure: true,
    rightsRole: {
      include: {
        rights: true,
      },
    },
    acExpoRightsRole: {
      include: {
        rights: true,
      },
    },
    commercial: true,
  },
});

export type MobileUserRole = "individual" | "professional" | "vigil" | "cashRegisterHolder";

export type MyUserType = Prisma.UserGetPayload<typeof myUserTypeValues> & {
  isRemoteControlAdmin?: Date;
  hasPassword?: boolean;
  hasSocialAccount?: boolean;
  hasAppleAccount?: boolean;
  userRole?: MobileUserRole;
};

export const userTypeValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    roles: true,
    userSharedInformation: true,
    picture: true,
    organiser: true,
  },
});

export type UserType = Prisma.UserGetPayload<typeof userTypeValues>;

export const userWithStructureTypeValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    ...userTypeValues.include,
    structure: {
      select: {
        id: true,
        hiddenFromExhibitorDirectory: true,
      },
    },
  },
});

export type UserWithStructureType = Prisma.UserGetPayload<typeof userWithStructureTypeValues>;

export const userShortTypeValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  select: {
    id: true,
    connectId: true,
    firstName: true,
    lastName: true,
    picture: true,
    interest: true,
  },
});

export type UserShortType = Prisma.UserGetPayload<typeof userShortTypeValues>;

export const detailledUserArgs = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    roles: true,
    userSharedInformation: true,
    picture: true,
    commercial: {
      include: {
        events: true,
      },
    },
    structure: {
      include: {
        socials: true,
        address: true,
        businessSectorRelation: true,
        eventStructures: {
          orderBy: {
            event: {
              createdAt: "desc",
            },
          },
          include: {
            event: {
              include: {
                dates: eventDateValues,
              },
            },
          },
        },
      },
    },
    organiser: {
      include: {
        events: {
          include: {
            dates: eventDateValues,
          },
          orderBy: {
            createdAt: "desc",
          },
        },
      },
    },
    tickets: {
      include: {
        ticketType: {
          include: {
            event: {
              include: {
                dates: eventDateValues,
              },
            },
          },
        },
      },
    },
    badges: {
      include: {
        badgeType: {
          include: {
            event: {
              include: {
                dates: eventDateValues,
              },
            },
          },
        },
      },
    },
  },
});

export type DetailledUser = Prisma.UserGetPayload<typeof detailledUserArgs>;

export interface UserTickets {
  ticket: EventDetails["ticketTypes"];
  event: EventForTickets;
  activity: EditableActivity | null;
}

export interface BaseTicket {
  id: string;
  hmac: string;
  code: string;
  ticketTypeId: string;
  user: User;
  userId: string;
  gateId?: string;
  createdAt: string;
  updatedAt: string;
}

export type UserTicketFormatted = BaseTicket & {
  id: string;
  ticket: {
    id: string;
    type: "activity" | "event";
    count: number;
    price: number;
    name: string;
    validityDates: string[];
  };
  ticketType: {
    id: string;
    name: string;
    event: UserMyTicket["ticketType"]["event"];
    activity: Activity | null;
    price: {
      priceTTC: number;
    };
    validityDates: string[];
    numberEntriesPerDay: number | null;
    isInfiniteEntries: boolean;
    isPaid: boolean;
    customAppearance: {
      color: string;
      xPicture: number;
      yPicture: number;
      hPicture: number;
      wPicture: number;
      order: string;
      picture: File | null;
    } | null;
    customPdfAppearance: {
      xBackgroundPicture: number;
      yBackgroundPicture: number;
      hBackgroundPicture: number;
      wBackgroundPicture: number;
      xHeaderPicture: number;
      yHeaderPicture: number;
      hHeaderPicture: number;
      wHeaderPicture: number;
      backgroundColor: string;
      headerColor: string;
      textColor: string;
      model: CustomPdfAppearanceModel;
      opacity: number;
      textPictures: File[];
      backgroundPicture: File | null;
      headerPicture: File | null;
      pdfSectionsOrder: CustomPdfAppearanceSection[];
      customSection1: string | null;
      customSection2: string | null;
    } | null;
    gateIds?: string[];
  };
  invoice: File | null;
};

export type TicketFormatted = UserTicketFormatted & { count: number };

export type MyPurchaseHistory = {
  name: string;
  event?: string | null;
  role?: Role | null;
  date: Date;
  total: number;
  invoiceLink?: string;
};

export type MySalesHistory = {
  eventName: string;
  date: Date | string;
  total: number;
  invoiceLink?: string;
};

export const userStoreBasketTypeValues = Prisma.validator<Prisma.UserDefaultArgs>()({
  include: {
    cart: {
      include: {
        basketItem: {
          include: {
            buyableItem: {
              include: {
                ticketType: true,
              },
            },
          },
        },
      },
      where: {
        type: BasketType.store,
        isExpired: false,
      },
    },
    tickets: {
      include: {
        ticketType: true,
        orderItem: true,
      },
    },
  },
});

export type UserStoreBasketTypeValues = Prisma.UserGetPayload<typeof userStoreBasketTypeValues>;

export type CompleteProfileBody = {
  firstName: string;
  lastName: string;
  isProfessional: boolean;
  siret?: string | null;
  vat?: string | null;
  businessName?: string | null;
  country?: string;
  acceptedTerms: boolean;
};
